import Resource from './Resource';
import { TYPE, STYLE } from 'Store/reducers/SystemNotificationReducer';
import { prospectingTracker, accountTracker } from 'App/babel/helpers/Tracker';
import RequestBuilder from './RequestBuilder';
import logError from '../helpers/logError';
import { getCountryFromProspectingId } from 'App/services/prospectingService';
import openModal from 'App/services/Modal/Modal';
import ProvisioningResource from 'App/babel/resources/Provisioning';
import store from 'Store/index';
import { getProspectingTokenCostFromState } from 'Store/selectors/AppSelectors';

export const TIER_DECLINED_ERROR = new Error('Tier upgrade declined');

class Prospecting extends Resource {
	constructor() {
		super('prospecting', null, { notificationTitle: 'default.account' });

		this.notifications = {
			save: () => {
				return {
					title: 'savedTitle.prospecting',
					style: STYLE.SUCCESS,
					icon: 'save',
					type: TYPE.BODY
				};
			},
			saveBulk: () => {
				return {
					title: 'savedTitle.prospectingMulti',
					body: 'saved.prospectingMulti',
					style: STYLE.SUCCESS,
					icon: 'save',
					type: TYPE.BODY,
					customBody: {
						extraSeconds: 2000
					}
				};
			},
			saveError: ({ error }) => {
				return {
					title: 'default.error',
					body: error.translated || 'saveError.prospecting',
					style: STYLE.ERROR,
					icon: 'save',
					type: TYPE.BODY
				};
			},
			saveBulkError: ({ error }) => {
				if (error?.key === 'NotEnoughCredits') {
					return null;
				}
				return {
					title: 'default.error',
					body: error.translated || 'saveError.prospectingMulti',
					style: STYLE.ERROR,
					icon: 'save',
					type: TYPE.BODY
				};
			},
			findError: ({ error }) => {
				return {
					title: 'default.error',
					body: error.translated || error.msg,
					style: STYLE.ERROR,
					icon: 'times',
					type: TYPE.BODY
				};
			}
		};
	}

	getCountryFromProspectingId(prospectingId) {
		return getCountryFromProspectingId(prospectingId);
	}

	lookup(field, params = {}) {
		return this._getRequest(`lookup/${field}`, { methodName: 'lookup', params }).then(response => response.data);
	}

	async getClient(prospectingId, country) {
		const fields = ['name', 'prospectingId', 'groupSize', 'dunsNo'];
		const prospectingRB = new RequestBuilder();
		prospectingRB.addFilter({ field: 'prospectingId' }, RequestBuilder.comparisonTypes.Equals, prospectingId);
		prospectingRB.extraParams.push({ key: 'country', value: country });
		prospectingRB.fields = fields;

		const res = await this.find(prospectingRB.build());
		return res.data?.length > 0 ? res.data[0] : {};
	}

	findGroupstructure(params = {}) {
		return this._getRequest('', {
			methodName: 'findGroupstructure',
			_url: 'prospectinggroupstructure',
			params
		}).then(response => response.data);
	}

	getCompaniesFromGroup(params = {}) {
		return this._getRequest('', {
			methodName: 'getCompaniesFromGroup',
			_url: 'getCompaniesFromGroup',
			params
		}).then(response => response.data);
	}

	findDirectors(data = {}) {
		return this._postRequest('findDirectors', data).then(response => response.data);
	}

	async getCompanyGroup(prospectingId, fetchClient = false) {
		let client;
		const country = this.getCountryFromProspectingId(prospectingId);
		if (fetchClient) {
			client = await this.getClient(prospectingId, country);
		}

		let total = 0;

		const fixMatchInUpsales = item => {
			total++;
			if (item.matchInUpsales) {
				Object.assign(item, item.matchInUpsales, { existing: item.matchInUpsales });
			}
			for (const branch of item.branches) {
				total++;
				if (branch.matchInUpsales) {
					Object.assign(branch, branch.matchInUpsales, { existing: branch.matchInUpsales });
				}
			}
			for (let child of item.children) {
				child = fixMatchInUpsales(child, total);
			}

			return item;
		};

		const getBranches = item => {
			let allBranches = item.branches;
			for (const child of item.children) {
				const childBranches = getBranches(child);
				allBranches = allBranches.concat(childBranches);
			}
			return allBranches;
		};
		const rb = new RequestBuilder();
		rb.addFilter({ field: 'prospectingId' }, RequestBuilder.comparisonTypes.Equals, prospectingId);
		rb.extraParams.push({ key: 'country', value: country });

		try {
			const group = await this.findGroupstructure(rb.build());

			if (group.data && group.data.length) {
				let tree = group.data[0];
				tree = fixMatchInUpsales(tree);
				const unknowns = tree ? getBranches(tree) : null;

				const data = {
					client: client || {},
					unknownTotal: unknowns.length,
					total: total,
					tree: tree,
					unknowns: unknowns
				};

				return data;
			}

			if (client) {
				return {
					client,
					unknownTotal: 0,
					total: 0,
					tree: null,
					unknowns: []
				};
			}
		} catch (err) {
			logError(err, 'failed getting group structure');
			return undefined;
		}
	}

	checkTier = (addedUsages = 1) =>
		ProvisioningResource.doSomething('getPotentialAddonCost', {
			addedUsages,
			type: 'cdh'
		})
			// Always resolves to an array — a failing tier lookup must not block the save
			.then(({ data }) => data ?? [])
			.catch(err => {
				logError(err, 'Failed to get tier');
				return [];
			});

	calculateTokenCost = prospectingIds => {
		const prospectingTokenCost = getProspectingTokenCostFromState(store.getState().App);

		if (!prospectingIds?.length || !prospectingTokenCost) {
			return 0;
		}

		const cost = prospectingIds.reduce((acc, prospectingId) => {
			const country = this.getCountryFromProspectingId(prospectingId);
			return acc + (prospectingTokenCost[country] ?? 1);
		}, 0);
		return cost;
	};

	async save(data, opts) {
		if (opts?.skipTierCheck) {
			return this.#handleSave(data, opts);
		}

		const tokenCost = this.calculateTokenCost([data.prospectingId]);
		const tierUpgrades = await this.checkTier(tokenCost);
		if (!tierUpgrades.length) {
			return this.#handleSave(data, opts);
		}

		await this.handleTierUpgrade(tierUpgrades);
		return this.#handleSave(data, opts);
	}

	#handleSave = async (data, opts) => {
		const result = await super.save(data, opts);
		const event = data.id ? accountTracker.events.UPDATE : accountTracker.events.CREATE;
		accountTracker.track(event, { module: 'Account & Contacts' });
		return result;
	};

	// This functions rejects the promise if the user declines the tier upgrade
	handleTierUpgrade = tierUpgrades => {
		return new Promise((resolve, reject) => {
			openModal('RequestTierUpgradeModal', {
				tierUpgrades,
				onClose: async approvedTier => {
					if (!approvedTier) {
						reject(TIER_DECLINED_ERROR);
						return;
					}
					return resolve();
				}
			});
		});
	};

	#handleSaveBulk = async (data, opts = {}) => {
		const result = await this._postRequest('', data, { ...opts, _url: 'prospectingbulk', methodName: 'saveBulk' });
		prospectingTracker.track(prospectingTracker.events.BULK_SAVE);
		return result.data;
	};

	async saveBulk(data, opts = {}) {
		const prospectingIdsFilter = data?.filters?.q?.find(
			filter => filter.a === 'prospectingId' && filter.c === 'eq'
		);
		// This assumes that the filter contains a prospectingId filter
		if (prospectingIdsFilter && !opts?.skipTierCheck) {
			const tokenCost = this.calculateTokenCost(prospectingIdsFilter.v);
			const tierUpgrades = await this.checkTier(tokenCost);
			if (tierUpgrades.length) {
				// This functions rejects the promise if the user declines the tier upgrade
				await this.handleTierUpgrade(tierUpgrades);
			}
		}
		return this.#handleSaveBulk(data, opts);
	}

	async matchAll(opts = {}) {
		const result = await this._putRequest(
			'',
			{},
			{ ...opts, _url: 'prospectingmatcher/all', methodName: 'matchAll' }
		);
		return result.data;
	}

	getSuggestionBoxes(filters, opts = {}) {
		return this._getRequest('', {
			params: filters,
			...opts,
			_url: 'prospectingsuggestion',
			methodName: 'getSuggestionBoxes'
		}).then(response => response.data);
	}

	findSuggestionBoxes(id, filters = {}, opts = {}) {
		return this._getRequest('', {
			params: filters,
			...opts,
			_url: `prospectingsuggestion/${id}`,
			methodName: 'getSuggestionBoxes'
		}).then(response => response.data);
	}

	runActionsSuggestions(id, data, opts = {}) {
		return this._postRequest('', data, {
			...opts,
			_url: `prospectingsuggestion/${id}`,
			methodName: 'runActionsSuggestions'
		}).then(response => response.data);
	}

	getPromoData(type, params = {}, opts = {}) {
		return this._getRequest(`promo/${type}`, {
			...opts,
			params,
			methodName: 'getPromoData'
		}).then(response => response.data);
	}

	getGroupStatement(prospectingId, params = {}, opts = {}) {
		return this._getRequest(`groupStatement/${prospectingId}`, {
			...opts,
			params,
			methodName: 'getGroupStatement'
		}).then(response => response.data);
	}

	ceoOnBoard({ limit, offset, sort, country, ...opts } = {}) {
		return this._getRequest('ceoOnBoard', {
			params: { limit, offset, sort, country },
			...opts,
			methodName: 'ceoOnBoard'
		}).then(response => response.data);
	}

	insideCompanyGroups({ limit, offset, sort, country, ...opts } = {}) {
		return this._getRequest('insideCompanyGroups', {
			params: { limit, offset, sort, country },
			...opts,
			methodName: 'insideCompanyGroups'
		}).then(response => response.data);
	}
}

export default new Prospecting();
